import { json, useNavigate, useParams } from "react-router-dom";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchMessages } from "../../../api/filings/chatAPI";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import ChatMessage from "./ChatMessage";
import ChatTypingMessage from "./ChatTypingMessage";
import SocketActions from "../../../lib/socketActions";
import { Constants } from "../../../lib/Constants";
import createWebSocketClient from "../../../services/sockets/WebSocketService";
import { Handler401 } from "../../../api/utils/responseActions";
import EmptyState from "../pages/EmptyState";
import { getRelativeTime } from "../../../utils/date/DateUtils";
import { useRefreshOnAuth } from "../../../hooks/useRefreshOnAuth";

function ChatBody() {
  const params = useParams();
  const filingUuid = params.filingUuid;

  const messagesContainer = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingSignalSentRef = useRef(false);
  const socketIdentity = useRef(null);
  const bottomRef = useRef(null);

  const [typing, setTyping] = useState(false);
  const [typingSource, setTypingSource] = useState(null);
  const [content, setContent] = useState("");
  const [messagesData, setMessagesData] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const navigate = useNavigate();

  // Send Typing Signal with Debounce
  const sendTypingSignal = async (typing) => {
    if (!socketRef.current) return;

    const msg = {
      type: SocketActions.TYPING,
      typing: typing,
      filingId: filingUuid,
    };

    socketRef.current.onConnect(() => {
      socketRef.current.sendMessage(msg);
    });
  };

  // Handle Typing Events with Debounce
  const chatMessageTypingHandler = (event) => {
    if (event.keyCode !== Constants.ENTER_KEY_CODE) {
      if (!isTypingSignalSentRef.current) {
        sendTypingSignal(true);
        isTypingSignalSentRef.current = true;
      }
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingSignal(false);
        isTypingSignalSentRef.current = false;
      }, 3000);
    } else {
      clearTimeout(typingTimeoutRef.current);
      isTypingSignalSentRef.current = false;
    }
  };

  const messageSubmitHandler = (event) => {
    event.preventDefault();
    if (content.trim() && socketRef.current) {
      const msg = {
        type: SocketActions.MESSAGE,
        message: content.trim(),
        filingId: filingUuid,
      };
      socketRef.current.onConnect(() => {
        socketRef.current.sendMessage(msg);
        setContent("");
      });
    }
  };

  // Initialize WebSocket connection ONCE when component mounts
  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = createWebSocketClient(
        `ws/chat/taxfiling/${filingUuid}`,
      );
    }

    // Cleanup function to close listeners and WebSocket when component unmounts
    return () => {
      if (socketRef.current) {
        // Remove all listeners BEFORE closing the socket
        socketRef.current.removeListener(SocketActions.MESSAGE);
        socketRef.current.removeListener(SocketActions.TYPING);
        socketRef.current.removeListener("socket_identity");

        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [filingUuid]);

  // Handle incoming WebSocket messages using listeners
  useEffect(() => {
    if (!socketRef.current) return;

    // Add listeners for different events (MESSAGE, TYPING, and socket_identity)
    socketRef.current.addListener("socket_identity", (data) => {
      socketIdentity.current = data.socket_identity; // Ensure state updates correctly
    });

    socketRef.current.addListener(SocketActions.TYPING, (data) => {
      if (socketIdentity.current !== data.socket_identity) {
        setTyping(data.typing);
        setTypingSource(data.socket_identity);
      }
    });

    socketRef.current.addListener(SocketActions.MESSAGE, (data) => {
      if (filingUuid === data.filingId) {
        const lastMessage = {
          content: data.message,
          name: data.name,
          date: data.date,
          time: data.time,
          source: data.socket_identity,
        };
        setMessagesData((prevMessages) => {
          const updatedMessages = prevMessages?.messages
            ? [...prevMessages.messages, lastMessage]
            : [lastMessage]; // Handle undefined case

          return { ...prevMessages, messages: updatedMessages };
        });
        setTyping(false);
      }
    });
  }, [filingUuid]); // Re-run when filingUuid or socketIdentity changes

  // Fetch messages when filingUuid changes
  const getMessages = useCallback(async () => {
    setLoadingMessages(true); // Optional: show ghost loader during re-sync
    try {
      const response = await fetchMessages("tax/messages", filingUuid);
      if (!response.ok) {
        if (response.status === 401) {
          Handler401(navigate);
          return;
        }
        return;
      }

      const data = await response.json();
      setMessagesData(data);
      setLoadingMessages(false);
    } catch (error) {
      console.error("Error fetching messages", error);
      setLoadingMessages(false);
    }
  }, [filingUuid, navigate]);

  // Define the refresh handler
  const handleAuthRefresh = useCallback(() => {
    // A. Re-fetch the history
    getMessages();

    // B. Re-initialize the WebSocket
    // By clearing socketRef.current and closing the old one,
    // the useEffect responsible for creation will trigger again.
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    // This force-clears typing signals
    setTyping(false);
    isTypingSignalSentRef.current = false;
  }, [getMessages]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(handleAuthRefresh);

  useEffect(() => {
    getMessages();
  }, [getMessages]);

  // Scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesData.messages]);

  return (
    <Fragment>
      {loadingMessages && (
        <div className="main-content-area">
          <div className="content">
            <LoadingPlaceholder rows={3} type="card-large" />
          </div>
        </div>
      )}
      {!loadingMessages && (
        <div id="chatPage">
          {messagesData.messages.length === 0 && (
            <div
              style={{
                margin: "0px auto",
                maxWidth: "550px",
                paddingBottom: "40px",
                marginTop: "80px",
                display: "grid",
                gap: "40px",
              }}
            >
              <EmptyState
                title="Let's get started"
                text="Start a conversation to make your filing process smoother."
              />
            </div>
          )}
          {messagesData.messages.length > 0 && (
            <div id="chat" ref={messagesContainer}>
              <div>
                {messagesData.messages.map((message, idx) => (
                  <React.Fragment key={idx}>
                    <ChatMessage
                      usertype={
                        message.usertype ?? // prefer API-provided
                        (message.source === socketIdentity.current
                          ? "userA"
                          : "userB") // fallback for live/socket messages
                      }
                      message={message.content}
                      username={message.name}
                      senttime={getRelativeTime(message.time)}
                      idx={idx}
                    />
                  </React.Fragment>
                ))}
                {typing && (
                  <ChatTypingMessage
                    usertype={
                      typingSource === socketIdentity.current
                        ? "userA"
                        : "userB"
                    }
                  />
                )}
                <div ref={bottomRef} />
              </div>
            </div>
          )}

          <div id="writterBox">
            <div>
              <textarea
                value={content}
                rows={1}
                placeholder="Write message..."
                onChange={(event) => setContent(event.target.value)}
                onKeyUp={chatMessageTypingHandler}
              />
              <button
                className="btn btnTag btnAfter"
                data-icon="send"
                onClick={messageSubmitHandler}
              />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}

export default ChatBody;
